/**
 * Bazaarvoice PDP global overrides (BV third-party DOM).
 * Rating summary (inline row + histogram popup) + full reviews section (`#product-bazaarvoice-reviews`).
 *
 * Figma refs:
 * - Inline row desktop: 11183:76926 | mobile: 11183:74762
 * - Sort dropdown button desktop: 11183:77799
 * - Histogram row desktop: 11183:77717 | mobile: 11183:75632
 * - Mobile filter/sort toolbar: 11183:75667
 */

import typography from '@amblique/king-living/app/theme/foundations/typography';

const bvShow = '[data-bv-show="rating_summary"]';
const bvRoot = `& .sf-bazaarvoice-inline-rating ${bvShow}`;
const bvMainContainer = `${bvRoot} .bv_main_container`;
const bvButtonsRow = `${bvRoot} .bv_rating_summary_buttons_container`;
const bvRatingButton = `${bvRoot} .bv_main_rating_button`;
const bvStarsWrap = `${bvRoot} .bv_stars_component_container`;
const bvScore = `${bvRoot} .bv_avgRating_component_container`;
const bvReviews = `${bvRoot} .bv_numReviews_text`;
const bvStripDesktop = `& [data-testid="add-to-cart-strip-desktop"] .sf-bazaarvoice-inline-rating[data-bv-variant="strip"] ${bvShow}`;
const bvHistogramPopup = [
  `${bvRoot} .histogram_hover`,
  '& [data-bv-show="rating_summary"] .histogram_hover',
  `${bvRoot} .bv_histogram`,
  '& .bv_modal_component_container[data-bv-modal] .bv_modal_outer_content',
  '& .bv_modal_outer_content',
].join(', ');
const bvModalOuter = [
  '& .bv_modal_component_container[data-bv-modal] .bv_modal_outer_content',
  '& .bv_modal_outer_content',
].join(', ');
const bvModalShell = [
  '& .bv_modal_component_container[data-bv-modal]',
  '& .bv_modal_component_container[data-bv-modal] .bv_modal_inner_content',
  '& .bv_modal_outer_content',
  '& .histogram_hover',
].join(', ');
const bvModalInner = [
  '& .bv_modal_component_container[data-bv-modal] .bv_modal_inner_content',
  '& .bv_modal_inner_content',
].join(', ');
const bvHistogramButtonWrap = [
  '& .bv_modal_outer_content .bv_button_component_container',
  '& .bv_button_component_container',
].join(', ');
const bvHistogramList = [
  `${bvRoot} .bv_histogram_list_container`,
  '& .histogram_hover .bv_histogram_list_container',
  '& .bv_modal_outer_content .bv_histogram_list_container',
].join(', ');
const bvHistogramRow = [
  `${bvRoot} .bv_histogram_row_container`,
  '& [data-bv-show="rating_summary"] .bv_histogram_row_container',
  '& .histogram_hover .bv_histogram_row_container',
  '& .bv_modal_outer_content .bv_histogram_row_container',
  '& .bv_modal_component_container[data-bv-modal] .bv_histogram_row_container',
].join(', ');
const bvHistogramPrefix = [
  `${bvRoot} .bv_histogram_row_prefix`,
  '& .histogram_hover .bv_histogram_row_prefix',
  '& .bv_modal_outer_content .bv_histogram_row_prefix',
  '& .bv_modal_component_container[data-bv-modal] .bv_histogram_row_prefix',
].join(', ');
const bvHistogramStar = [
  `${bvRoot} .bv_histogram_row_star`,
  '& .histogram_hover .bv_histogram_row_star',
  '& .bv_modal_outer_content .bv_histogram_row_star',
].join(', ');
const bvHistogramBar = [
  `${bvRoot} .bv_histogram_row_full_bar`,
  '& .histogram_hover .bv_histogram_row_full_bar',
  '& .bv_modal_outer_content .bv_histogram_row_full_bar',
].join(', ');
const bvHistogramBarFill = [
  `${bvRoot} .bv_histogram_row_bar`,
  '& .histogram_hover .bv_histogram_row_bar',
  '& .bv_modal_outer_content .bv_histogram_row_bar',
].join(', ');
const bvHistogramCount = [
  `${bvRoot} .bv_histogram_row_rating`,
  '& .histogram_hover .bv_histogram_row_rating',
  '& .bv_modal_outer_content .bv_histogram_row_rating',
].join(', ');
const bvHistogramReadReviews = [
  `${bvRoot} .bv_button_buttonFull`,
  `${bvRoot} button.bv_button_buttonFull`,
  `${bvRoot} .bv_button_button_full`,
  `${bvRoot} a.bv_button_button_full`,
  `${bvRoot} button.bv_button_button_full`,
  '& .histogram_hover .bv_button_buttonFull',
  '& .histogram_hover button.bv_button_buttonFull',
  '& .histogram_hover .bv_button_button_full',
  '& .histogram_hover a.bv_button_button_full',
  '& .histogram_hover button.bv_button_button_full',
  '& .bv_modal_outer_content .bv_button_buttonFull',
  '& .bv_modal_outer_content button.bv_button_buttonFull',
  '& .bv_modal_outer_content .bv_button_button_full',
  '& .bv_modal_outer_content a.bv_button_button_full',
  '& .bv_modal_outer_content button.bv_button_button_full',
  '& .bv_button_component_container button',
].join(', ');
const bvReviewsSection = '& #product-bazaarvoice-reviews';
/** Force King Living typography — BV ships serif body/heading stacks in styled-components. */
const bvFontFamilyReset = {
  fontFamily: 'body !important',
};
const bvReviewsFontReset = `${bvReviewsSection}, ${bvReviewsSection} *`;
const bvInlineRatingFontReset = `${bvRoot}, ${bvRoot} *`;
const bvReviewsPopupFontReset = '& div[id^="bv-popup-"], & div[id^="bv-popup-"] *';
const bvReviewsTitle = `${bvReviewsSection} #bv_review_maincontainer h2`;
const bvReviewsContentHeader = `${bvReviewsSection} [data-bv-v="contentHeader"]`;
/** Mobile filter + sort toolbar (BV `sc-107gs2s-*` / `sc-1kd7i6j-*`). Figma 11183:75667 */
const bvReviewsMobileToolbarRow = `${bvReviewsSection} div[class*="bv-rnr__sc-107gs2s-2"]`;
const bvReviewsMobileToolbarCell = `${bvReviewsMobileToolbarRow} > div[class*="bv-rnr__sc-107gs2s-3"]`;
const bvReviewsMobileToolbarButton = `${bvReviewsSection} div[class*="bv-rnr__sc-1kd7i6j-0"][role="button"]`;
const bvReviewsMobileToolbarButtonIcon = `${bvReviewsMobileToolbarButton} > span`;
const bvReviewsMobileToolbarButtonBody = `${bvReviewsMobileToolbarButton} > div`;
const bvReviewsMobileToolbarSortLabel = `${bvReviewsMobileToolbarButton} div[class*="bv-rnr__wnep33-2"]`;
const bvReviewsMobileToolbarSortValue = `${bvReviewsMobileToolbarButton} div[class*="bv-rnr__wnep33-3"]`;
const bvReviewsSortStack = `${bvReviewsSection} div[class*="bv-rnr__wnep33"]`;
const bvReviewsSortInner = `${bvReviewsSortStack} > div`;
const bvReviewsSortDropdown = `${bvReviewsSection} div[class*="bv-rnr__sc-13ijx62-0"]`;
const bvReviewsSortShell = [
  `${bvReviewsSection} div[class*="bv-rnr__sc-13ijx62-1"]`,
  `${bvReviewsSection} div[class*="bv-rnr__sc-13ijx62-7"]`,
].join(', ');
const bvReviewsSortControl = `${bvReviewsSection} div[class*="bv-rnr__sc-13ijx62-3"]`;
const bvReviewsSortLabel = `${bvReviewsSection} span[class*="bv-rnr__sc-13ijx62-4"]`;
const bvReviewsSortCombobox = `${bvReviewsSection} div[class*="bv-rnr__sc-13ijx62-5"][role="combobox"]`;
const bvReviewsRelevancyWrap = `${bvReviewsSection} div[class*="bv-rnr__sc-1vjvgwp-0"]`;
const bvRelevancyInfo = [
  `${bvReviewsRelevancyWrap} button`,
  `${bvReviewsSection} button[class*="bv-rnr__sc-1vjvgwp-1"]`,
].join(', ');
const bvRelevancySrOnly = `${bvReviewsSection} span[class*="bv-rnr__bm6gry"]`;
/** BV Relevancy info popover — id suffix is dynamic (`bv-popup-47`, etc.). */
const bvRelevancyPopup = [
  `${bvReviewsSection} div[id^="bv-popup-"]`,
  '& div[id^="bv-popup-"]',
].join(', ');
const bvRelevancyPopupClose = `${bvRelevancyPopup} .PopoverClose`;
const bvReviewsStarsAbbr = `${bvReviewsSection} abbr[aria-hidden="true"]`;
const bvVerifiedBadge = `${bvReviewsSection} button:has(.badge-label)`;
const bvVerifiedBadgeLabel = `${bvVerifiedBadge} .badge-label`;
const bvReviewsPaginationPrev = `${bvReviewsSection} .prev`;
const bvReviewsPaginationNext = `${bvReviewsSection} .next`;
const bvReviewsPaginationPrevDisabled =
  `${bvReviewsPaginationPrev}:disabled, ${bvReviewsPaginationPrev}[aria-disabled="true"], ${bvReviewsPaginationPrev}.disabled`;
const bvReviewsPaginationNextDisabled =
  `${bvReviewsPaginationNext}:disabled, ${bvReviewsPaginationNext}[aria-disabled="true"], ${bvReviewsPaginationNext}.disabled`;

/** Figma KL-Designs 11183:77933 (disabled) / 11183:77934 (active) — box only; keep BV arrow SVG. */
const bvReviewsPaginationButtonBase = {
  alignItems: 'center',
  backgroundColor: 'white',
  border: '1px solid #E8E8E8',
  borderRadius: '4px',
  boxSizing: 'border-box',
  cursor: 'pointer',
  display: 'inline-flex',
  flexShrink: 0,
  height: '52px',
  justifyContent: 'center',
  lineHeight: '1',
  minHeight: '52px',
  minWidth: '52px',
  padding: '16px',
  textDecoration: 'none',
  width: '52px',
  '& > span, & > div': {
    alignItems: 'center',
    display: 'flex',
    height: '20px',
    justifyContent: 'center',
    margin: '0',
    padding: '0',
    width: '20px',
  },
  '& svg': {
    display: 'block',
    flexShrink: 0,
    height: '20px',
    maxHeight: '20px',
    maxWidth: '20px',
    width: '20px',
  },
};

/** 13px stars — BV renders 20px SVGs inline in `.bv_stars_component_container`. */
const bvStarClip = {
  [bvStarsWrap]: {
    alignItems: 'center',
    display: 'inline-flex',
    flexShrink: 0,
    flexWrap: 'nowrap',
    gap: '4px',
    height: '13px !important',
    lineHeight: 0,
    margin: '0 !important',
    padding: '0 !important',
    '& svg': {
      display: 'block',
      flexShrink: 0,
      height: '13px !important',
      margin: '0 !important',
      padding: '0 !important',
      paddingRight: '4px !important',
      width: '13px !important',

      '&:last-child': {
        paddingRight: '0 !important',
      },
    },
  },
};

export const bazaarvoicePdpGlobalStyles = {
  ...bvStarClip,
  [bvReviewsFontReset]: bvFontFamilyReset,
  [bvInlineRatingFontReset]: bvFontFamilyReset,
  [bvReviewsPopupFontReset]: bvFontFamilyReset,
  [bvMainContainer]: {
    alignItems: 'center',
    background: 'transparent !important',
    backgroundColor: 'transparent !important',
    border: 'none !important',
    borderStyle: 'none !important',
    display: 'inline-flex !important',
    flexWrap: 'nowrap',
    gap: '8px',
    margin: '0 !important',
    padding: '0 !important',
    width: 'auto',
  },
  [bvButtonsRow]: {
    alignItems: 'center',
    background: 'transparent !important',
    backgroundColor: 'transparent !important',
    display: 'inline-flex',
    flexWrap: 'nowrap',
    gap: '8px !important',
  },
  [bvRatingButton]: {
    alignItems: 'center',
    background: 'transparent !important',
    backgroundColor: 'transparent !important',
    border: 'none !important',
    borderStyle: 'none !important',
    color: 'inherit !important',
    cursor: 'pointer',
    display: 'inline-flex',
    flexShrink: 0,
    flexWrap: 'nowrap',
    gap: '8px !important',
    margin: '0 !important',
    padding: '0 !important',
    textDecoration: 'none !important',
    '&:hover, &:focus, &:active': {
      background: 'transparent !important',
      backgroundColor: 'transparent !important',
      border: 'none !important',
      boxShadow: 'none !important',
    },
  },
  [bvScore]: {
    ...typography.textStyles.bodyS,
    alignItems: 'baseline',
    color: '#282829 !important',
    display: 'inline-flex',
    flexShrink: 0,
    fontSize: {base: '13px !important', lg: '12px !important'},
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    letterSpacing: '0 !important',
    lineHeight: '120% !important',
    margin: '0 !important',
    padding: '0 !important',
    whiteSpace: 'nowrap',
    '&::after': {
      content: '"Stars"',
      marginLeft: '3px',
    },
  },
  [`${bvRoot} .bv_numReviews_component_container`]: {
    background: 'transparent !important',
    backgroundColor: 'transparent !important',
    display: 'inline-flex',
    flexShrink: 0,
    margin: '0 !important',
    padding: '0 !important',
  },
  [`${bvRoot} .bv_offscreen_text`]: {
    border: '0',
    clip: 'rect(0 0 0 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: '0',
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: '1px',
  },
  [bvReviews]: {
    ...typography.textStyles.labelS,
    color: '#8C683B !important',
    fontSize: {base: '13px !important', lg: '12px !important'},
    fontStyle: 'normal !important',
    fontWeight: '600 !important',
    letterSpacing: '0 !important',
    lineHeight: '120% !important',
    textDecoration: 'underline !important',
    textDecorationLine: 'underline !important',
    textDecorationSkipInk: 'none !important',
    textDecorationStyle: 'solid !important',
    textUnderlineOffset: '35% !important',
    textUnderlinePosition: 'from-font !important',
    whiteSpace: 'nowrap',
  },
  [`${bvStripDesktop} .bv_avgRating_component_container`]: {
    color: 'rgba(27, 26, 24, 0.7) !important',
    fontSize: '14px !important',
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    letterSpacing: '0 !important',
    lineHeight: '1.4 !important',
    '&::after': {
      content: '"Stars"',
      marginLeft: '3px',
    },
  },
  [`${bvStripDesktop} .bv_numReviews_text`]: {
    fontSize: '13px !important',
    lineHeight: '1.2 !important',
  },
  [bvModalShell]: {
    borderRadius: '0 !important',
    overflow: 'visible !important',
  },
  [bvModalInner]: {
    margin: '0 !important',
    padding: '0 !important',
  },
  [bvModalOuter]: {
    backgroundColor: 'white !important',
    border: '1px solid #282829 !important',
    borderRadius: '0 !important',
    boxShadow: '0 4px 16px rgba(27, 26, 24, 0.12) !important',
    boxSizing: 'border-box !important',
    color: '#282829 !important',
    display: 'block !important',
    flexWrap: 'nowrap !important',
    fontFamily: 'body',
    margin: '0 !important',
    maxWidth: {base: '280px !important', lg: '317px !important'},
    padding: {base: '12px 16px !important', lg: '12px 16px !important'},
    width: 'auto !important',
    zIndex: 'popover',
    '& .bv_histogram, & ul': {
      margin: '0 !important',
      padding: '0 !important',
      width: '100% !important',
    },
  },
  [bvHistogramPopup]: {
    backgroundColor: 'white',
    border: '1px solid #282829',
    borderRadius: '0 !important',
    boxShadow: '0 4px 16px rgba(27, 26, 24, 0.12)',
    boxSizing: 'border-box',
    color: '#282829',
    fontFamily: 'body',
    maxWidth: {base: '280px', lg: '317px'},
    padding: {base: '12px 16px', lg: '12px 16px'},
    zIndex: 'popover',
  },
  [bvHistogramList]: {
    display: 'block !important',
    listStyle: 'none !important',
    margin: '0 !important',
    padding: '0 !important',
    width: '100% !important',
    '& + .bv_histogram_list_container': {
      marginTop: '8px !important',
    },
  },
  [bvHistogramRow]: {
    alignItems: 'center !important',
    color: '#000000 !important',
    display: 'flex !important',
    flexDirection: 'row !important',
    flexWrap: 'nowrap !important',
    fontFamily: 'body',
    fontSize: {base: '13px !important', lg: '12px !important'},
    fontWeight: '600 !important',
    gap: '8px !important',
    letterSpacing: '0 !important',
    lineHeight: '120% !important',
    margin: '0 !important',
    minHeight: '20px !important',
    padding: '0 !important',
    textDecoration: 'none !important',
    whiteSpace: 'nowrap !important',
    width: '100% !important',
  },
  [bvHistogramPrefix]: {
    alignItems: 'center !important',
    display: 'inline-flex !important',
    flexDirection: 'row !important',
    flexShrink: '0 !important',
    gap: '4px !important',
    margin: '0 !important',
    minWidth: '28px !important',
    padding: '0 !important',
    verticalAlign: 'middle !important',
    whiteSpace: 'nowrap !important',
    width: 'auto !important',
    '& > *': {
      display: 'inline-flex !important',
      flexShrink: '0 !important',
    },
  },
  [bvHistogramStar]: {
    alignItems: 'center',
    display: 'inline-flex',
    flexShrink: 0,
    lineHeight: 0,
    '& svg': {
      display: 'block',
      flexShrink: 0,
      height: '16px !important',
      width: '16px !important',
    },
  },
  [bvHistogramBar]: {
    backgroundColor: '#F0F0F0 !important',
    borderRadius: '100px !important',
    boxShadow: 'none !important',
    flex: '1 1 auto !important',
    height: '8px !important',
    margin: '0 !important',
    maxWidth: '100% !important',
    minWidth: '0 !important',
    overflow: 'hidden !important',
    padding: '0 !important',
    position: 'relative !important',
    width: 'auto !important',
  },
  [bvHistogramBarFill]: {
    backgroundColor: 'rgba(27, 26, 24, 0.9) !important',
    borderRadius: '100px !important',
    height: '100% !important',
    minWidth: '0 !important',
  },
  [bvHistogramCount]: {
    color: '#000000 !important',
    flexShrink: '0 !important',
    fontFamily: 'body',
    fontSize: {base: '13px !important', lg: '12px !important'},
    fontWeight: {base: '400 !important', lg: '600 !important'},
    letterSpacing: '0 !important',
    lineHeight: '120% !important',
    minWidth: '24px',
    textAlign: 'right !important',
    whiteSpace: 'nowrap !important',
  },
  [bvHistogramButtonWrap]: {
    marginTop: '12px !important',
    padding: '0 !important',
    textAlign: 'center !important',
    width: '100% !important',
  },
  [bvHistogramReadReviews]: {
    alignItems: 'center',
    backgroundColor: '#1B1A18 !important',
    backgroundImage: 'none !important',
    border: '1px solid #1B1A18 !important',
    borderRadius: '0 !important',
    boxShadow: 'none !important',
    boxSizing: 'border-box !important',
    color: 'white !important',
    cursor: 'pointer',
    display: 'inline-flex !important',
    flexShrink: '0 !important',
    fontFamily: 'body',
    fontSize: {base: '14px !important', lg: '14px !important'},
    fontWeight: '600 !important',
    height: '44px !important',
    justifyContent: 'center',
    letterSpacing: '0 !important',
    lineHeight: '1.4 !important',
    margin: '0 auto !important',
    maxHeight: '44px !important',
    minHeight: '44px !important',
    minWidth: '165px !important',
    padding: '0 16px !important',
    textAlign: 'center',
    textDecoration: 'none !important',
    transform: 'none !important',
    transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease !important',
    width: 'auto !important',
    '&:hover, &:focus, &:active': {
      backgroundColor: '#282829 !important',
      backgroundImage: 'none !important',
      border: '1px solid #282829 !important',
      boxShadow: 'none !important',
      color: 'white !important',
      fontSize: {base: '14px !important', lg: '14px !important'},
      height: '44px !important',
      maxHeight: '44px !important',
      minHeight: '44px !important',
      minWidth: '165px !important',
      padding: '0 16px !important',
      textDecoration: 'none !important',
      transform: 'none !important',
      width: 'auto !important',
    },
  },
  '& [data-testid="add-to-cart-strip-mobile"] .sf-bazaarvoice-inline-rating, .sf-bazaarvoice-inline-rating:not(:has(.bv_rating_summary_buttons_container)), .sf-bazaarvoice-inline-rating:has(.bv_hide_visibility)': {
    display: 'none',
  },
  [`${bvReviewsSection}:not(:has([data-bv-v="contentHeader"])), ${bvReviewsSection}:has(.bv_hide_visibility)`]: {
    display: 'none',
    margin: 0,
    padding: 0,
  },
  [bvReviewsSection]: {
    backgroundColor: 'white',
    paddingBottom: {base: '48px', lg: '100px'},
    paddingTop: {base: '48px', lg: '100px'},
    marginTop: {base: '-60px', lg: '-64px'},
    '& > div': {
      margin: '0 auto',
      maxWidth: '1440px',
      paddingX: {base: '10px', lg: '80px'},
    },
  },
  [bvReviewsTitle]: {
    ...typography.textStyles.headingXL,
    color: 'brandColors.greyscale90',
    fontFamily: 'heading',
    fontSize: {base: '32px', lg: '40px'},
    fontStyle: 'normal',
    fontWeight: '500',
    letterSpacing: {base: '-0.32px', lg: '-0.4px'},
    lineHeight: '1.2',
    margin: '0 0 44px 0',
  },
  [bvReviewsContentHeader]: {
    alignItems: {base: 'flex-start', lg: 'center'},
    display: 'flex',
    flexDirection: {base: 'column', lg: 'row'},
    flexWrap: 'wrap',
    gap: {base: '12px', lg: '16px'},
    justifyContent: 'space-between',
    width: '100%',
  },
  [bvReviewsMobileToolbarRow]: {
    alignItems: 'stretch !important',
    display: 'flex !important',
    flexDirection: 'row !important',
    gap: '8px !important',
    margin: {base: '0 0 16px 0 !important', lg: '0 !important'},
    padding: '0 !important',
    width: '100% !important',
  },
  [bvReviewsMobileToolbarCell]: {
    display: 'flex !important',
    flex: {base: '1 1 50% !important', lg: 'unset'},
    height: 'auto !important',
    margin: '0 !important',
    minHeight: '0 !important',
    minWidth: '0 !important',
    padding: '0 !important',
    width: {base: '100% !important', lg: 'auto !important'},
  },
  [bvReviewsMobileToolbarButton]: {
    alignItems: 'center !important',
    alignSelf: 'stretch !important',
    backgroundColor: 'white !important',
    border: '1px solid rgba(27, 26, 24, 0.9) !important',
    borderRadius: '4px !important',
    boxSizing: 'border-box !important',
    color: '#1B1A18 !important',
    cursor: 'pointer !important',
    display: 'flex !important',
    flex: '1 1 auto !important',
    flexDirection: 'row !important',
    flexWrap: 'nowrap !important',
    gap: '8px !important',
    height: 'auto !important',
    justifyContent: 'center !important',
    margin: '0 !important',
    maxHeight: 'none !important',
    minHeight: {base: '44px !important', lg: '0 !important'},
    padding: {base: '10px 12px !important', lg: 'unset'},
    width: '100% !important',
  },
  [`${bvReviewsMobileToolbarCell}:first-child ${bvReviewsMobileToolbarButton}`]: {
    borderRadius: {base: '4px 0 0 4px !important', lg: 'unset'},
    borderRightWidth: {base: '0 !important', lg: 'unset'},
  },
  [`${bvReviewsMobileToolbarCell}:last-child ${bvReviewsMobileToolbarButton}`]: {
    borderRadius: {base: '0 4px 4px 0 !important', lg: 'unset'},
  },
  
  [bvReviewsMobileToolbarButtonIcon]: {
    alignItems: 'center !important',
    display: 'flex !important',
    flexShrink: '0 !important',
    lineHeight: '0 !important',
    margin: '0 !important',
    padding: '0 !important',
    '& svg': {
      display: 'block !important',
      flexShrink: '0 !important',
      height: '17px !important',
      width: '17px !important',
    },
  },
  [bvReviewsMobileToolbarButtonBody]: {
    alignItems: {base: 'center !important', lg: 'unset'},
    display: 'flex !important',
    flexDirection: 'column !important',
    gap: '2px !important',
    justifyContent: 'center !important',
    margin: '0 !important',
    minWidth: '0 !important',
    padding: '0 !important',
    textAlign: 'center !important',
    width: '100% !important',
    '&:not(:has(> div))': {
      ...typography.textStyles.labelS,
      fontSize: '13px !important',
      fontWeight: '600 !important',
      lineHeight: '1.2 !important',
    },
  },
  [bvReviewsMobileToolbarSortLabel]: {
    ...typography.textStyles.labelS,
    color: '#1B1A18 !important',
    fontSize: '13px !important',
    fontWeight: '600 !important',
    lineHeight: '1.2 !important',
    margin: '0 !important',
    padding: '0 !important',
    textAlign: 'left !important',
    whiteSpace: 'nowrap !important',
  },
  [bvReviewsMobileToolbarSortValue]: {
    ...typography.textStyles.bodyS,
    color: '#1B1A18 !important',
    fontSize: '13px !important',
    fontWeight: '400 !important',
    lineHeight: '1.2 !important',
    margin: '0 !important',
    padding: '0 !important',
    textAlign: 'left !important',
    whiteSpace: 'nowrap !important',
  },
  [`${bvReviewsMobileToolbarButton}:has(div[class*="bv-rnr__wnep33-2"])`]: {
    justifyContent: 'flex-start !important',
    padding: {base: '10px 16px !important', lg: 'unset'},
  },
  [`${bvReviewsMobileToolbarRow} ${bvReviewsSortStack}`]: {
    display: 'flex !important',
    flex: '1 1 auto !important',
    margin: '0 !important',
    width: '100% !important',
  },
  [bvReviewsSortStack]: {
    alignItems: 'center !important',
    display: 'flex !important',
    flexDirection: 'row !important',
    flexShrink: '0 !important',
    flexWrap: 'wrap !important',
    gap: '16px !important',
    justifyContent: 'flex-end !important',
    lineHeight: 'normal !important',
    margin: {base: '0 !important', lg: '0 0 39px 0 !important'},
    padding: '0 !important',
    width: {base: '100% !important', lg: 'auto !important'},
    justifyContent: 'flex-start !important',
  },
  [bvReviewsSortInner]: {
    alignItems: 'center !important',
    display: 'flex !important',
    flexDirection: 'row !important',
    flexWrap: 'wrap !important',
    gap: '16px !important',
    margin: '0 !important',
    padding: '0 !important',
    width: 'auto !important',
  },
  [bvReviewsSortDropdown]: {
    alignItems: 'center !important',
    alignSelf: 'center !important',
    display: 'inline-flex !important',
    margin: '0 !important',
    padding: '0 !important',
    width: 'auto !important',
  },
  [bvReviewsSortShell]: {
    alignItems: 'center !important',
    alignSelf: 'center !important',
    display: 'flex !important',
    margin: '0 !important',
    padding: '0 !important',
    width: 'auto !important',
  },
  [bvReviewsSortControl]: {
    alignItems: 'center !important',
    alignSelf: 'center !important',
    backgroundColor: 'white !important',
    border: '1px solid rgba(27, 26, 24, 0.9) !important',
    borderRadius: '4px !important',
    boxSizing: 'border-box !important',
    cursor: 'pointer !important',
    display: 'inline-flex !important',
    flexDirection: 'row !important',
    flexShrink: '0 !important',
    flexWrap: 'nowrap !important',
    gap: '0 !important',
    justifyContent: 'center !important',
    margin: '0 !important',
    minHeight: '0 !important',
    padding: {base: '10px 16px !important', lg: '12px 24px !important'},
    width: 'auto !important',
    '& > div': {
      alignItems: 'center !important',
      alignSelf: 'center !important',
      display: 'flex !important',
      flexShrink: '0 !important',
      margin: '0 !important',
      padding: '0 !important',
    },
    '& > div:has(svg)': {
      alignItems: 'center !important',
      display: 'flex !important',
      flexShrink: '0 !important',
      lineHeight: '0 !important',
      margin: '0 0 0 8px !important',
      padding: '0 !important',
    },
    '& svg': {
      display: 'block !important',
      flexShrink: '0 !important',
      height: '12px !important',
      margin: '0 !important',
      width: '12px !important',
    },
  },
  [bvReviewsSortLabel]: {
    ...typography.textStyles.labelS,
    alignItems: 'center !important',
    alignSelf: 'center !important',
    color: '#1B1A18 !important',
    display: 'inline-flex !important',
    fontSize: {base: '13px !important', lg: '12px !important'},
    fontStyle: 'normal !important',
    fontWeight: '600 !important',
    letterSpacing: '0 !important',
    lineHeight: '1.2 !important',
    margin: '0 !important',
    padding: '0 !important',
    textTransform: 'none !important',
    whiteSpace: 'nowrap',
    '&::after': {
      content: '": "',
    },
  },
  [bvReviewsSortCombobox]: {
    ...typography.textStyles.labelS,
    alignItems: 'center !important',
    alignSelf: 'center !important',
    background: 'transparent !important',
    border: 'none !important',
    boxShadow: 'none !important',
    color: '#1B1A18 !important',
    cursor: 'pointer !important',
    display: 'inline-flex !important',
    fontSize: {base: '13px !important', lg: '12px !important'},
    fontStyle: 'normal !important',
    fontWeight: '600 !important',
    letterSpacing: '0 !important',
    lineHeight: '1.2 !important',
    marginTop: '0 !important',
    marginRight: '0 !important',
    marginBottom: '0 !important',
    marginLeft: '5px !important',
    minHeight: '0 !important',
    outline: 'none !important',
    padding: '0 !important',
    textTransform: 'none !important',
    width: 'auto !important',
  },
  [`${bvReviewsSortCombobox} span, ${bvReviewsSortCombobox} div`]: {
    alignItems: 'center !important',
    color: 'inherit !important',
    display: 'inline-flex !important',
    fontSize: 'inherit !important',
    fontWeight: 'inherit !important',
    lineHeight: 'inherit !important',
    margin: '0 !important',
    padding: '0 !important',
    textTransform: 'inherit !important',
  },
  [bvReviewsRelevancyWrap]: {
    alignItems: 'center !important',
    alignSelf: 'center !important',
    display: 'inline-flex !important',
    flexShrink: '0 !important',
    margin: '0 !important',
    padding: '0 !important',
    width: 'auto !important',
    '& > span': {
      alignItems: 'center !important',
      display: 'inline-flex !important',
      margin: '0 !important',
      padding: '0 !important',
      width: 'auto !important',
    },
  },
  [bvRelevancySrOnly]: {
    border: '0 !important',
    clip: 'rect(0 0 0 0) !important',
    height: '1px !important',
    margin: '-1px !important',
    overflow: 'hidden !important',
    padding: '0 !important',
    position: 'absolute !important',
    whiteSpace: 'nowrap !important',
    width: '1px !important',
  },
  [bvRelevancyInfo]: {
    alignItems: 'center !important',
    alignSelf: 'center !important',
    background: 'transparent !important',
    border: 'none !important',
    boxShadow: 'none !important',
    color: '#282829 !important',
    cursor: 'pointer !important',
    display: 'inline-flex !important',
    flexDirection: 'row !important',
    flexShrink: '0 !important',
    flexWrap: 'nowrap !important',
    fontFamily: 'body',
    fontSize: '12px !important',
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    gap: '4px !important',
    lineHeight: '1.2 !important',
    margin: '0 !important',
    padding: '0 !important',
    textDecoration: 'none !important',
    textTransform: 'none !important',
    whiteSpace: 'nowrap !important',
    width: 'auto !important',
    '& span': {
      alignItems: 'center !important',
      color: 'inherit !important',
      display: 'inline-flex !important',
      fontFamily: 'inherit !important',
      fontSize: 'inherit !important',
      fontWeight: 'inherit !important',
      gap: '4px !important',
      lineHeight: 'inherit !important',
      margin: '0 !important',
      padding: '0 !important',
      textDecoration: 'none !important',
      textTransform: 'none !important',
    },
    '& span[class*="bv-rnr"]': {
      alignItems: 'center !important',
      color: '#282829 !important',
      display: 'inline-flex !important',
      fontSize: '12px !important',
      fontWeight: '400 !important',
      gap: '4px !important',
      lineHeight: '1.2 !important',
      textDecoration: 'none !important',
    },
    '& svg': {
      display: 'block !important',
      flexShrink: '0 !important',
      height: '16px !important',
      maxHeight: '16px !important',
      maxWidth: '16px !important',
      width: '16px !important',
    },
    '&:hover, &:focus': {
      background: 'transparent !important',
      color: '#282829 !important',
      textDecoration: 'none !important',
    },
  },
  [bvRelevancyPopup]: {
    whiteSpace: 'normal !important',
    width: '295px !important',
    left: {base: '0 !important', lg: 'unset'},
    '& > div > div:first-child': {
      left: {base: '45px !important', lg: 'unset'},
    },
  },

  [bvRelevancyPopupClose]: {
    float: 'right !important',
  },
  [`${bvReviewsSection} div:has(> button abbr[aria-hidden="true"])`]: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: '8px',
  },
  [`${bvReviewsSection} [data-bv-v="ratingSummaryContainer"]`]: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: '8px',
  },
  [`${bvReviewsSection} [data-bv-v="ratingSummaryStars"]`]: {
    display: 'inline-flex',
    flexShrink: 0,
    flexWrap: 'nowrap',
    width: 'auto',
  },
  [`${bvReviewsSection} button:has(abbr[aria-hidden="true"])`]: {
    display: 'inline-flex',
    flexShrink: 0,
    width: 'auto',
  },
  [`${bvReviewsSection} button:has(abbr[aria-hidden="true"]) > div`]: {
    alignItems: 'center',
    display: 'inline-flex',
    flexWrap: 'nowrap',
    width: 'auto',
  },
  [bvReviewsStarsAbbr]: {
    alignItems: 'center',
    borderBottom: 'none',
    display: 'inline-flex',
    flexDirection: 'row',
    flexShrink: 0,
    flexWrap: 'nowrap',
    gap: '2px',
    height: 'auto',
    lineHeight: 0,
    maxWidth: 'none',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    width: 'auto',
    '& svg': {
      display: 'block',
      flexShrink: 0,
      height: '16px',
      width: '16px',
    },
  },
  [bvVerifiedBadge]: {
    alignItems: 'center',
    alignSelf: 'center',
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    display: 'inline-flex',
    flexDirection: 'row',
    flexShrink: 0,
    flexWrap: 'nowrap',
    gap: '4px',
    height: 'auto',
    margin: '0',
    minHeight: '0',
    padding: '0',
    verticalAlign: 'middle',
    width: 'auto',
    '& svg': {
      display: 'block',
      flexShrink: 0,
      height: '16px',
      margin: '0',
      marginTop: '-10px',
      overflow: 'visible',
      width: '16px',
    },
  },
  [bvVerifiedBadgeLabel]: {
    color: '#282829 !important',
    fontFamily: 'body',
    fontSize: '10px !important',
    fontStyle: 'normal !important',
    fontWeight: '600 !important',
    letterSpacing: '0.04em !important',
    lineHeight: '1 !important',
    margin: '0 !important',
    padding: '0 !important',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  [`${bvReviewsSection} ul:has(> li .prev)`]: {
    display: 'flex',
    gap: '8px',
    listStyle: 'none',
    margin: '0',
    padding: '0',
  },
  [`${bvReviewsSection} ul:has(> li .prev) > li`]: {
    display: 'flex',
    listStyle: 'none',
    margin: '0',
    padding: '0',
  },
  [bvReviewsPaginationPrev]: {
    ...bvReviewsPaginationButtonBase,
  },
  [bvReviewsPaginationNext]: {
    ...bvReviewsPaginationButtonBase,
  },
  [bvReviewsPaginationPrevDisabled]: {
    backgroundColor: '#E8E8E8',
    border: 'none',
    cursor: 'not-allowed',
    pointerEvents: 'none',
    '& svg': {
      opacity: 0.5,
    },
  },
  [bvReviewsPaginationNextDisabled]: {
    backgroundColor: '#E8E8E8',
    border: 'none',
    cursor: 'not-allowed',
    pointerEvents: 'none',
    '& svg': {
      opacity: 0.5,
    },
  },
};
